import {
  TFormattedJobLogLine,
  TJobLogLevel,
} from '@/console/features/jobs/components/job-logs/job-logs.types';
import { DATE_TIME_FORMAT_LOCAL_STORAGE_KEY } from '@/shared/features/header/components/date-time-format/date-time-format.constants';
import { getBrowserDateTimeFormat } from '@/shared/utils/format';

const normalizeJobLogTimestamp = (timestamp: string) => {
  const separatorIndex = timestamp.indexOf(' ');

  if (separatorIndex === -1) {
    return timestamp;
  }

  const datePart = timestamp.slice(0, separatorIndex);
  const timePart = timestamp.slice(separatorIndex + 1).replace(',', '.');

  return `${datePart}T${timePart}`;
};

const parseJobLogLine = (logLine: string) => {
  const [timestamp, severityAndMessage] = logLine.split(' - ');

  if (!timestamp || !severityAndMessage) {
    return;
  }

  const severitySeparatorIndex = severityAndMessage.indexOf(': ');

  if (severitySeparatorIndex === -1) {
    return;
  }

  return {
    timestamp,
    level: severityAndMessage.slice(0, severitySeparatorIndex) as TJobLogLevel,
    message: severityAndMessage.slice(severitySeparatorIndex + 2),
  };
};

const formatSingleJobLogLine = (message: string, colorClassName?: string) => ({
  text: message,
  colorClassName,
});

const getJobLogsDateTimeFormat = () => {
  const browserFormat = getBrowserDateTimeFormat();
  const storedFormat = localStorage.getItem(DATE_TIME_FORMAT_LOCAL_STORAGE_KEY);
  const preferredFormat = storedFormat || browserFormat || 'dd/MM/yyyy HH:mm';
  const usesAmPm = /(^|[^a-zA-Z])a+([^a-zA-Z]|$)/.test(preferredFormat);
  const hourTokenIndex = preferredFormat.search(/[hHkK]/);
  const datePart =
    hourTokenIndex > 0
      ? preferredFormat.slice(0, hourTokenIndex).trim()
      : 'yyyy-MM-dd';

  return `${datePart} ${usesAmPm ? 'hh:mm:ss.SSS a' : 'HH:mm:ss.SSS'}`.trim();
};

export const formatJobLogLine = (
  logLine: string,
  shouldConvertTimestamp: boolean,
  formatLocalTime: (
    utcTimestamp?: string | number,
    timeZone?: string,
    customFormat?: string,
  ) => string,
): TFormattedJobLogLine[] => {
  const parsedLogLine = parseJobLogLine(logLine);

  if (!parsedLogLine) {
    return logLine
      .split('\n')
      .filter((line) => line.length > 0)
      .map((line) => formatSingleJobLogLine(line));
  }

  const formattedTimestamp = formatLocalTime(
    normalizeJobLogTimestamp(parsedLogLine.timestamp),
    undefined,
    getJobLogsDateTimeFormat(),
  );
  const colorClassName = getJobLogColorClassName(parsedLogLine.level);
  const messageLines = parsedLogLine.message.split('\n');
  const timestamp = shouldConvertTimestamp
    ? formattedTimestamp
    : parsedLogLine.timestamp.replace(',', '.');

  return [
    formatSingleJobLogLine(
      `${timestamp} ${parsedLogLine.level}: ${messageLines[0] ?? ''}`,
      colorClassName,
    ),
    ...messageLines
      .slice(1)
      .map((line) => formatSingleJobLogLine(line, colorClassName)),
  ];
};

const getJobLogColorClassName = (level?: TJobLogLevel) => {
  switch (level) {
    case 'INFO':
      return 'job-log-line-info';
    case 'ERROR':
      return 'job-log-line-error';
    case 'WARNING':
      return 'job-log-line-warning';
    default:
      return undefined;
  }
};
